/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { ArticleCard } from '../ArticleCard/ArticleCard';
import { ArticleListInput, SHOW_AS } from './ArticlesList.types';
import { LinkButton } from '../LinkButton/LinkButton';
import { formatDateMonthYear } from '@/utils/date-formatting';
import { host } from '@/config';
import { ArticlePreview } from '@/types/article';
import { frontCoverPageSize } from '@/config';

type CategoryLike = {
  id?: string;
  title?: string;
  data?: { url?: string };
  value?: {
    id?: string;
    title?: string;
    data?: { url?: string };
  };
};

const getCategoryId = (category?: CategoryLike) => category?.id ?? category?.value?.id;
const getCategoryUrl = (category?: CategoryLike) => category?.data?.url ?? category?.value?.data?.url;
const getCategoryTitle = (category?: CategoryLike) => category?.title ?? category?.value?.title;
const normalizeCategoryKey = (value?: string) => value?.trim().toLowerCase();
const getCategoryMatchKeys = (category?: CategoryLike | string): Set<string> => {
  const keys = new Set<string>();

  if (!category) return keys;

  if (typeof category === 'string') {
    const normalized = normalizeCategoryKey(category);
    if (normalized) keys.add(normalized);
    return keys;
  }

  const id = normalizeCategoryKey(getCategoryId(category));
  const url = normalizeCategoryKey(getCategoryUrl(category));
  const title = normalizeCategoryKey(getCategoryTitle(category));

  if (id) keys.add(id);
  if (url) keys.add(url);
  if (title) keys.add(title);

  return keys;
};
const hasCategoryIntersection = (left: Set<string>, right: Set<string>) => {
  for (const key of left) {
    if (right.has(key)) return true;
  }
  return false;
};

const HubListRow: React.FC<{ article: ArticlePreview; buttonText: string }> = ({ article, buttonText }) => (
  <div className="flex flex-col gap-2 md:flex-row md:items-center">
    <div className="flex flex-1 flex-col gap-2">
      <p className="text-dark my-0 text-[16px] leading-[1.4] font-semibold md:text-[18px]">{article.title}</p>
      {article.lastUpdated && (
        <p className="text-dark my-0 text-[16px] leading-[1.4] font-normal md:text-[18px] md:font-medium">
          {formatDateMonthYear(new Date(article.lastUpdated))}
        </p>
      )}
    </div>
    <a
      href={`${host}${article.url}`}
      aria-label={`${buttonText}: ${article.title}`}
      className="border-dark-100 text-dark-200 flex h-[50px] w-[237px] shrink-0 items-center justify-center rounded-md border bg-white text-[18px] font-bold md:h-[60px]"
    >
      {buttonText}
    </a>
  </div>
);

export const ArticlesList: React.FC<ArticleListInput> = ({
  title,
  showAs = SHOW_AS.CARDS,
  buttonText = 'Go to article',
  category,
  builderState,
  tagVariant = 'primary',
}) => {
  const {
    articles,
    currentPage = 1,
    totalPages = 1,
    urlPrefix,
    clientSideFiltering,
    pageSize,
  } = builderState.state.articles || {};
  const selectedCategoryKeys = getCategoryMatchKeys(category as CategoryLike | undefined);
  const filteredArticleItems = (articles ?? []).filter((article) => {
    if (selectedCategoryKeys.size === 0) return true;
    const articleCategoryKeys = getCategoryMatchKeys(article.category as CategoryLike | string | undefined);
    return hasCategoryIntersection(selectedCategoryKeys, articleCategoryKeys);
  });
  const effectivePageSize = pageSize ?? frontCoverPageSize;
  const effectiveTotalPages = clientSideFiltering
    ? Math.max(1, Math.ceil(filteredArticleItems.length / effectivePageSize))
    : totalPages;
  const effectiveCurrentPage = Math.min(currentPage, effectiveTotalPages);
  const articleItems = clientSideFiltering
    ? filteredArticleItems.slice(
        (effectiveCurrentPage - 1) * effectivePageSize,
        effectiveCurrentPage * effectivePageSize
      )
    : filteredArticleItems;

  if (!articleItems.length && showAs !== SHOW_AS.HUB_LIST) return null;

  const getPageNumbers = (): (number | 'dots')[] => {
    const pages: (number | 'dots')[] = [];

    if (effectiveTotalPages <= 5) {
      for (let i = 1; i <= effectiveTotalPages; i++) pages.push(i);
      return pages;
    }

    if (effectiveCurrentPage <= 3) {
      pages.push(1, 2, 3, 4, 'dots', effectiveTotalPages);
    } else if (effectiveCurrentPage >= effectiveTotalPages - 2) {
      pages.push(
        1,
        'dots',
        effectiveTotalPages - 3,
        effectiveTotalPages - 2,
        effectiveTotalPages - 1,
        effectiveTotalPages
      );
    } else {
      pages.push(
        1,
        'dots',
        effectiveCurrentPage - 1,
        effectiveCurrentPage,
        effectiveCurrentPage + 1,
        'dots',
        effectiveTotalPages
      );
    }

    return pages;
  };

  const renderPagination = () => {
    if (effectiveTotalPages <= 1) return null;

    return (
      <div
        className={`flex flex-wrap items-center justify-center ${showAs === SHOW_AS.HUB_LIST ? 'gap-4 md:gap-10' : 'my-12 gap-10'}`}
      >
        <a
          data-testid="pagination-prev"
          href={
            effectiveCurrentPage > 1
              ? `${urlPrefix}${effectiveCurrentPage === 2 ? '' : `/page/${effectiveCurrentPage - 1}`}`
              : '#'
          }
          className={`text-dark-100 hover:text-dark ${effectiveCurrentPage <= 1 ? 'pointer-events-none opacity-40' : ''}`}
          aria-disabled={effectiveCurrentPage <= 1}
          aria-label="Previous page"
        >
          <IconChevronLeft size={16} aria-hidden="true" />
        </a>

        {getPageNumbers().map((item, idx) => {
          if (item === 'dots') {
            return (
              <span key={`dots-${idx}`} className="text-dark-100 px-2 font-semibold">
                ...
              </span>
            );
          }

          const isActive = item === effectiveCurrentPage;
          const isLastPage = item === effectiveTotalPages;

          return (
            <a
              key={item}
              href={item !== 1 ? `${urlPrefix}/page/${item}` : `${urlPrefix}`}
              data-testid={isLastPage ? 'pagination-last-page' : `pagination-page-${item}`}
              className={`font-semibold ${isActive ? 'text-dark' : 'text-dark-100 hover:text-dark'}`}
            >
              {item}
            </a>
          );
        })}

        <a
          data-testid="pagination-next"
          href={effectiveCurrentPage < effectiveTotalPages ? `${urlPrefix}/page/${effectiveCurrentPage + 1}` : '#'}
          className={`text-dark-100 hover:text-dark ${effectiveCurrentPage >= effectiveTotalPages ? 'pointer-events-none opacity-40' : ''}`}
          aria-disabled={effectiveCurrentPage >= effectiveTotalPages}
          aria-label="Next page"
        >
          <IconChevronRight size={16} aria-hidden="true" />
        </a>
      </div>
    );
  };

  const renderArticles = () => {
    if (showAs === SHOW_AS.CARDS) {
      return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {articleItems.map((article) => (
            <ArticleCard key={article.url} article={article} tagVariant={tagVariant} />
          ))}
        </div>
      );
    }

    if (showAs === SHOW_AS.HUB_LIST) {
      if (!articleItems.length) {
        return <p className="text-dark-100 my-0">No articles found for the selected category.</p>;
      }

      return (
        <div className="flex flex-col gap-4">
          {articleItems.map((article) => (
            <HubListRow key={article.url} article={article} buttonText={buttonText} />
          ))}
        </div>
      );
    }

    return (
      <ul className="pl-0">
        {articleItems.map((article) => (
          <li key={article.url} className="flex justify-between">
            <div>
              <h3 className="mb-1 text-xl font-bold">{article.title}</h3>
              {article.lastUpdated && <small>{formatDateMonthYear(new Date(article.lastUpdated))}</small>}
            </div>
            <div>
              <LinkButton
                link={article.url}
                label={'Go to article'}
                aria-label={`Go to article: ${article.title}`}
                size={'medium'}
                variant={'tertiary'}
              />
            </div>
          </li>
        ))}
      </ul>
    );
  };

  if (showAs === SHOW_AS.HUB_LIST) {
    return (
      <div
        data-testid="article-list"
        className="tc-container bg-background-200 flex flex-col gap-4 py-6 md:gap-8 md:py-8"
      >
        {title && (
          <h2
            data-testid="article-list-title"
            className="tc-subheading my-0 text-wrap"
            dangerouslySetInnerHTML={{ __html: title }}
          />
        )}
        {renderArticles()}
        {renderPagination()}
      </div>
    );
  }

  return (
    <div data-testid="article-list">
      {title && <h2 data-testid="article-list-title">{title}</h2>}
      {renderArticles()}
      {renderPagination()}
    </div>
  );
};
